import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import MaterialReactDataTable from "../../components/layout/MaterialReactDataTable";
import colorConfigs from "../../configs/colorConfigs";
import { AppAPiUrl, appModelPermission, appModelType } from "../../configs/appData";
import AxiosMain from "../../components/layout/AxiosMain";
import LoaderScreen from "../../components/layout/LoaderScreen";
import TranslationGetHOC from "../../components/layout/TranslationGetHOC";
import AppPermissionHOC from "../../components/layout/AppPermissionHOC";

interface SimCardValues {
  icc: string;
  imsi: string;         // not required anymore, but still in the form
  puk1: string;
  puk2: string;
  acc: string;          // optional
  organization: string; // if empty on create => default "1"
  available: number;    // store 0 or 1 in the form
  carrier: string;      // store carrier ID
  owner: string;        // store owner ID
}

function SimCards(props: any) {
  const navigate = useNavigate();

  // Redux states
  const { appCountry } = useSelector((state: RootState) => state.appState);

  // Translations
  const [iComponetTranslate, setICTranslation] = useState<any[]>([]);

  // Table states
  const [triggerUpdate, setTriggerUpdateData] = useState(false);

  // Modals
  const [openAddSimCardModal, setOpenAddSimCardModal] = useState(false);

  // CSV modal
  const [openImportCSVModal, setOpenImportCSVModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  // Dropdown data states
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [owners, setOwners] = useState<any[]>([]);

  // --- NEW STATES FOR EDITING LOGIC ---
  const [isEditMode, setIsEditMode] = useState(false);
  const [editSimCardId, setEditSimCardId] = useState<string | null>(null);

  // Load translations
  useEffect(() => {
    const { translateDataft } = props;
    const newCode = appCountry.toLowerCase();
    if (translateDataft && translateDataft.hasOwnProperty(newCode)) {
      setICTranslation(translateDataft[newCode]);
    } else {
      setICTranslation([]);
    }
  }, [props, appCountry]);

  // Fetch dropdown data once when component loads
  useEffect(() => {
    AxiosMain.get(AppAPiUrl.OrganizationList)
      .then((res) => {
        setOrganizations(res.data || []);
      })
      .catch((err) => console.error("Error fetching organizations:", err));

    AxiosMain.get(AppAPiUrl.CarrierList)
      .then((res) => {
        setCarriers(res.data || []);
      })
      .catch((err) => console.error("Error fetching carriers:", err));

    AxiosMain.get(AppAPiUrl.OwnerList)
      .then((res) => {
        setOwners(res.data || []);
      })
      .catch((err) => console.error("Error fetching owners:", err));
  }, []);

  // Helper for translations
  const t = (name: string) => {
    const ifExist: any = iComponetTranslate.filter((item: any) => item.label === name);
    if (ifExist.length > 0) {
      return ifExist[0].value ? ifExist[0].value : `No translate found (${ifExist[0].label})`;
    }
    return `No key found (${name})`;
  };

  // Table columns
  const simCardListsRow = [
    {
      accessorFn: (row: any) => row.icc,
      header: t("ICC"),
    },
    {
      accessorFn: (row: any) => row.imsi,
      header: t("IMSI"),
    },
    {
      accessorFn: (row: any) => row.puk1,
      header: t("PUK1"),
    },
    {
      accessorFn: (row: any) => row.puk2,
      header: t("PUK2"),
    },
    {
      accessorFn: (row: any) => row.acc,
      header: t("ACC"),
    },
    {
      accessorFn: (row: any) => row.organization?.name,
      header: t("Organization"),
    },
    {
      accessorFn: (row: any) => (row.available ? "Available" : "Not Available"),
      header: t("Available"),
    },
    {
      accessorFn: (row: any) => row.carrier?.name,
      header: t("Carrier"),
    },
    {
      accessorFn: (row: any) => row.owner?.name,
      header: t("Owner"),
    },
  ];

  const resetTrigger = () => {
    setTriggerUpdateData(false);
  };

  // Updated schema
  const simCardSchema = Yup.object().shape({
    icc: Yup.string().required("ICC is required"),
    imsi: Yup.string(), // no longer required
    puk1: Yup.string().required("PUK1 is required"),
    puk2: Yup.string().required("PUK2 is required"),
    acc: Yup.string(),  // optional
    organization: Yup.string(), // optional now
    available: Yup.number().oneOf([0, 1], "Must be 0 or 1").required("Available is required"),
    carrier: Yup.string().required("Carrier is required"),
    owner: Yup.string().required("Owner is required"),
  });

  // Formik
  const simCardForm = useFormik<SimCardValues>({
    initialValues: {
      icc: "",
      imsi: "",
      puk1: "",
      puk2: "",
      acc: "",
      organization: "",
      available: 1,
      carrier: "",
      owner: "",
    },
    validationSchema: simCardSchema,
    onSubmit: (values) => {
      // If org is empty on create, default to "1"
      // (We also apply this for edit below if you prefer the same logic)
      const fixedOrganization = values.organization ? values.organization : "1";

      if (isEditMode && editSimCardId) {
        // Update all fields
        const patchPayload = {
          ...values,
          available: values.available === 1,
          organization: fixedOrganization,
        };

        AxiosMain.patch(`${AppAPiUrl.SimCardUpdate}/${editSimCardId}/`, patchPayload)
          .then((res) => {
            if (res.status === 200 || res.status === 202) {
              Swal.fire({
                text: "SIM Card updated successfully",
                icon: "success",
              });
              handleCloseModal();
              setTriggerUpdateData(true);
            }
          })
          .catch(() => {
            Swal.fire({
              text: "Error updating SIM Card",
              icon: "error",
            });
          });
      } else {
        // Creating a brand new SIM card
        const payload = {
          ...values,
          available: values.available === 1,
          organization: fixedOrganization,
        };

        AxiosMain.post(AppAPiUrl.SimCardCreate, payload)
          .then((res) => {
            if (res.status === 201) {
              Swal.fire({
                text: "SIM Card added successfully",
                icon: "success",
              });
              handleCloseModal();
              setTriggerUpdateData(true);
            }
          })
          .catch(() => {
            Swal.fire({
              text: "Error adding SIM Card",
              icon: "error",
            });
          });
      }
    },
  });

  // Close modal helper
  const handleCloseModal = () => {
    simCardForm.resetForm();
    setOpenAddSimCardModal(false);
    setIsEditMode(false);
    setEditSimCardId(null);
  };

  // Updated getpopDate function to open the modal in Edit mode
  const getpopDate = (row: any) => {
    setIsEditMode(true);
    setEditSimCardId(row.id);

    // Pre-populate the form
    simCardForm.setValues({
      icc: row.icc || "",
      imsi: row.imsi || "",
      puk1: row.puk1 || "",
      puk2: row.puk2 || "",
      acc: row.acc || "",
      organization: row.organization?.id || "",
      available: row.available ? 1 : 0,
      carrier: row.carrier?.id || "",
      owner: row.owner?.id || "",
    });

    // Open the modal
    setOpenAddSimCardModal(true);
  };

  // CSV logic
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
    }
  };

  const downloadSampleCSV = () => {
    const headers = ["ICC", "IMSI", "puk1", "puk2", "acc", "organization", "available", "carrier", "owner"];
    const csvContent = headers.join(",") + "\n"; // no data rows
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = () => {
    if (!csvFile) {
      Swal.fire({
        text: "Please select a CSV file first",
        icon: "warning",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64String = (e.target?.result as string)?.split(",")[1]; // remove "data:...base64," prefix

      try {
        const response = await AxiosMain.post(AppAPiUrl.SimCardBulkImport, {
          csv_file: base64String,
        });

        const { success_count, failure_count, failed_rows, existing_count } = response.data;
        Swal.fire({
          title: "Import Success",
          text: `Success: ${success_count}, Failures: ${failure_count}, Existing: ${existing_count}`,
          icon: "success",
        });

        setOpenImportCSVModal(false);
        setCsvFile(null);
        setTriggerUpdateData(true);

        if (failed_rows && failed_rows.length > 0) {
          console.log("Failed Rows: ", failed_rows);
        }
      } catch (err) {
        Swal.fire({
          text: "Error importing CSV",
          icon: "error",
        });
      }
    };

    reader.readAsDataURL(csvFile);
  };

  return (
    <Box sx={{ my: "20px" }}>
      {/* Buttons row */}
      <div
        className="text-right"
        style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
      >
        <Button
          onClick={() => {
            // Open the modal in ADD mode
            setIsEditMode(false);
            setEditSimCardId(null);
            simCardForm.resetForm();
            setOpenAddSimCardModal(true);
          }}
          variant="contained"
          endIcon={<AddIcon />}
          className="cm-btn-style"
          sx={{
            color: colorConfigs.btnStyle.color,
            background: colorConfigs.btnColor.bgColorGreen,
            "&:hover": {
              color: colorConfigs.btnColor.colorGreen,
              background: colorConfigs.btnColor.hoverbgWhite,
            },
          }}
        >
          {t('add_sim_card')}
        </Button>

        <Button
          onClick={() => setOpenImportCSVModal(true)}
          variant="contained"
          className="cm-btn-style"
          sx={{
            color: colorConfigs.btnStyle.color,
            background: colorConfigs.btnColor.bgColorGreen,
            "&:hover": {
              color: colorConfigs.btnColor.colorGreen,
              background: colorConfigs.btnColor.hoverbgWhite,
            },
          }}
        >
          {t('import_from_csv')}
        </Button>
      </div>

      {/* SIM Cards Table */}
      <div style={{ marginTop: "20px", marginBottom: "20px" }}>
        <MaterialReactDataTable
          useFor={"SIM Cards"}
          apiLink={AppAPiUrl.SimCardList}
          tRow={simCardListsRow}
          clickFunc={getpopDate}
          triggerUpdate={triggerUpdate}
          resetTrigger={resetTrigger}
          clickFuncDataShow={true}
        />
      </div>

      {/* Modal for ADD / EDIT SIM CARD */}
      <Modal
        open={openAddSimCardModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <h2 style={{ margin: 0 }}>
              {isEditMode ? t("edit_sim_card") : t("add_sim_card")}
            </h2>
            <CloseIcon style={{ cursor: "pointer" }} onClick={handleCloseModal} />
          </Stack>

          <form onSubmit={simCardForm.handleSubmit} style={{ marginTop: "20px" }}>
            {/* ICC (required) */}
            <TextField
              fullWidth
              label={t("ICC")}
              name="icc"
              margin="normal"
              value={simCardForm.values.icc}
              onChange={simCardForm.handleChange}
              error={simCardForm.touched.icc && Boolean(simCardForm.errors.icc)}
              helperText={simCardForm.touched.icc && simCardForm.errors.icc}
              disabled={isEditMode}
            />

            {/* IMSI (optional) */}
            <TextField
              fullWidth
              label={t("IMSI")}
              name="imsi"
              margin="normal"
              value={simCardForm.values.imsi}
              onChange={simCardForm.handleChange}
              error={simCardForm.touched.imsi && Boolean(simCardForm.errors.imsi)}
              helperText={simCardForm.touched.imsi && simCardForm.errors.imsi}
            />

            {/* PUK1 (required) */}
            <TextField
              fullWidth
              label={t("PUK1")}
              name="puk1"
              margin="normal"
              value={simCardForm.values.puk1}
              onChange={simCardForm.handleChange}
              error={simCardForm.touched.puk1 && Boolean(simCardForm.errors.puk1)}
              helperText={simCardForm.touched.puk1 && simCardForm.errors.puk1}
            />

            {/* PUK2 (required) */}
            <TextField
              fullWidth
              label={t("PUK2")}
              name="puk2"
              margin="normal"
              value={simCardForm.values.puk2}
              onChange={simCardForm.handleChange}
              error={simCardForm.touched.puk2 && Boolean(simCardForm.errors.puk2)}
              helperText={simCardForm.touched.puk2 && simCardForm.errors.puk2}
            />

            {/* ACC (optional) */}
            <TextField
              fullWidth
              label={t("ACC")}
              name="acc"
              margin="normal"
              value={simCardForm.values.acc}
              onChange={simCardForm.handleChange}
              error={simCardForm.touched.acc && Boolean(simCardForm.errors.acc)}
              helperText={simCardForm.touched.acc && simCardForm.errors.acc}
            />

            {/* Organization (optional) */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="organization-label">{t('Organization')}</InputLabel>
              <Select
                labelId="organization-label"
                id="organization"
                label={t("Organization")}
                name="organization"
                value={simCardForm.values.organization}
                onChange={simCardForm.handleChange}
                error={
                  simCardForm.touched.organization &&
                  Boolean(simCardForm.errors.organization)
                }
                disabled={isEditMode} // or remove if you want to allow editing
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Available (required) */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="available-label">{t('Available')}</InputLabel>
              <Select
                labelId="available-label"
                id="available"
                label={t("Available")}
                name="available"
                value={simCardForm.values.available}
                onChange={simCardForm.handleChange}
                error={
                  simCardForm.touched.available &&
                  Boolean(simCardForm.errors.available)
                }
              >
                <MenuItem value={1}>Available</MenuItem>
                <MenuItem value={0}>Not Available</MenuItem>
              </Select>
            </FormControl>

            {/* Carrier (required) */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="carrier-label">{t('Carrier')}</InputLabel>
              <Select
                labelId="carrier-label"
                id="carrier"
                label={t("Carrier")}
                name="carrier"
                value={simCardForm.values.carrier}
                onChange={simCardForm.handleChange}
                error={simCardForm.touched.carrier && Boolean(simCardForm.errors.carrier)}
              >
                {carriers.map((carrier) => (
                  <MenuItem key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Owner (required) */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="owner-label">Owner</InputLabel>
              <Select
                labelId="owner-label"
                id="owner"
                label={t("Owner")}
                name="owner"
                value={simCardForm.values.owner}
                onChange={simCardForm.handleChange}
                error={simCardForm.touched.owner && Boolean(simCardForm.errors.owner)}
              >
                {owners.map((owner) => (
                  <MenuItem key={owner.id} value={owner.id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              sx={{
                mt: 2,
                color: colorConfigs.btnStyle.color,
                background: colorConfigs.btnColor.bgColorGreen,
                "&:hover": {
                  color: colorConfigs.btnColor.colorGreen,
                  background: colorConfigs.btnColor.hoverbgWhite,
                },
              }}
            >
              {isEditMode ? t("Update") : t("Save")}
            </Button>
          </form>
        </Box>
      </Modal>

      {/* Modal for IMPORT FROM CSV */}
      <Modal
        open={openImportCSVModal}
        onClose={() => setOpenImportCSVModal(false)}
        aria-labelledby="csv-modal-title"
        aria-describedby="csv-modal-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <h2 style={{ margin: 0 }}>Import from CSV</h2>
            <CloseIcon style={{ cursor: "pointer" }} onClick={() => setOpenImportCSVModal(false)} />
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={downloadSampleCSV} sx={{ mb: 2 }}>
             { t('download_sample_CSV')}
            </Button>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: "block", marginBottom: "10px" }}
            />

            <Button
              variant="contained"
              onClick={handleCSVUpload}
              sx={{
                color: colorConfigs.btnStyle.color,
                background: colorConfigs.btnColor.bgColorGreen,
                "&:hover": {
                  color: colorConfigs.btnColor.colorGreen,
                  background: colorConfigs.btnColor.hoverbgWhite,
                },
              }}
            >
              {t('Upload_CSV')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

// Wrap component with permission HOC
function SimCard({ isAllow }: any) {
  const dependency = { require: ["Sidebar"] };
  const MainComponent = TranslationGetHOC(
    SimCards,
    "simcard",
    isAllow,
    {},
    dependency
  );
  return isAllow ? <MainComponent /> : <LoaderScreen msg="Loading ..." />;
}

// Wrap the component with permission HOC if needed
export default AppPermissionHOC(SimCard, appModelPermission.SIMCARDS, appModelType.READ);

